import { type ComponentType, useEffect, useRef } from 'react';

// Radix modal layers set `pointer-events: none` on <body> and only clear it when the
// layer unmounts, after the close animation. Until then the first click on the page
// is swallowed. Release the body as soon as a dialog starts closing, unless another
// modal is still open on top of the page.
const releaseBodyPointerEvents = () => {
  // After React has applied the close, so the closing dialog no longer reads "open".
  // A timer rather than requestAnimationFrame, which is paused in background tabs.
  window.setTimeout(() => {
    const anotherModalOpen = document.querySelector(
      '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]',
    );
    if (!anotherModalOpen && document.body.style.pointerEvents === 'none') {
      document.body.style.pointerEvents = '';
    }
  }, 0);
};

type RootProps = { open?: boolean; onOpenChange?: (open: boolean) => void };

export const withReleaseBodyOnClose = <P extends RootProps>(Root: ComponentType<P>) => {
  const Wrapped = (props: P) => {
    const wasOpen = useRef(props.open);
    useEffect(() => {
      // Controlled close (open prop set to false by the caller, e.g. after saving).
      if (wasOpen.current && props.open === false) releaseBodyPointerEvents();
      wasOpen.current = props.open;
    }, [props.open]);

    return (
      <Root
        {...props}
        onOpenChange={(open: boolean) => {
          // Uncontrolled or user close (Escape, overlay, close button).
          if (!open) releaseBodyPointerEvents();
          props.onOpenChange?.(open);
        }}
      />
    );
  };
  Wrapped.displayName = `WithReleaseBodyOnClose(${Root.displayName ?? 'Root'})`;
  return Wrapped;
};
